import { DestroyRef, Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { Clock, DsLoggingService, HeartbeatPayloadModel, HeartbeatState, ShutdownReason } from '@cubigo/digital-signage';

import { fromEvent, merge } from 'rxjs';

import { AuthService } from './auth.service';
import { DeviceCensusService } from './device-census.service';
import { NativeDeviceInformationModel } from 'app/shared/models';
import { FeedHealthService } from './feed-health.service';

const MEMORY_PRESSURE_RATIO = 0.7;
const MEMORY_PRESSURE_HEARTBEATS = 2;

// The shell's half of the heartbeat — device, auth and feed health — the only way to spot a screen in trouble without visiting it.
@Injectable({ providedIn: 'root' })
export class DisplayHealthService {
  private readonly deviceCensus = inject(DeviceCensusService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly logging = inject(DsLoggingService);
  private readonly auth = inject(AuthService);
  private readonly clock = inject(Clock);
  private readonly feeds = inject(FeedHealthService);
  private runtimeSample?: () => Partial<HeartbeatPayloadModel>;
  private started = false;
  private offlineEvents = 0;
  private pressuredHeartbeats = 0;

  public attach(sample: () => Partial<HeartbeatPayloadModel>): () => void {
    this.runtimeSample = sample;

    return (): void => {
      if (this.runtimeSample === sample) {
        this.runtimeSample = undefined;
      }
    };
  }

  public start(): void {
    if (this.started) {
      return;
    }

    this.started = true;
    const heartbeat = this.clock.every(5 * 60_000, 0, () => this.heartbeat());
    const snapshot = this.clock.every(10_000, 0, () => {
      this.logging.snapshotSession({ ...this.sample() });
    });
    this.destroyRef.onDestroy(() => {
      heartbeat.dispose();
      snapshot.dispose();
      this.runtimeSample = undefined;
    });
    this.heartbeat();
    merge(fromEvent(window, 'online'), fromEvent(window, HeartbeatState.Offline))
      .pipe(takeUntilDestroyed(this.destroyRef)).subscribe(event => {
        if (event.type === HeartbeatState.Offline) {
          this.offlineEvents++;
        }

        this.logging.trackEvent(event.type === 'online' ? 'network_online' : 'network_offline');
        this.heartbeat();
      });
    fromEvent<PageTransitionEvent>(window, 'pageshow').pipe(takeUntilDestroyed(this.destroyRef)).subscribe(event => {
      if (event.persisted) {
        this.logging.beginSession();
        this.heartbeat();
      }
    });
    fromEvent<ErrorEvent>(window, 'error').pipe(takeUntilDestroyed(this.destroyRef)).subscribe(event => {
      this.logging.logError(event.error);
    });
    fromEvent<PromiseRejectionEvent>(window, 'unhandledrejection')
      .pipe(takeUntilDestroyed(this.destroyRef)).subscribe(event => {
        this.logging.logError(event.reason);
      });
  }

  private heartbeat(): void {
    const sample = this.sample();
    this.logging.trackHeartbeat(sample);
    this.relieveMemoryPressure(sample);
  }

  // A renderer that runs out of heap dies with no reload and no telemetry, so the board goes dark until someone visits it.
  // Two samples in a row, so a single spike before a garbage collection does not cost a reload.
  private relieveMemoryPressure(sample: HeartbeatPayloadModel): void {
    const { usedJsHeapMb, heapLimitMb } = sample;
    const pressured = typeof usedJsHeapMb === 'number' && typeof heapLimitMb === 'number'
      && usedJsHeapMb > MEMORY_PRESSURE_RATIO * heapLimitMb;
    this.pressuredHeartbeats = pressured ? this.pressuredHeartbeats + 1 : 0;

    // Offline, the reload could land on the browser's error page, which is worse than the pressure.
    if (this.pressuredHeartbeats < MEMORY_PRESSURE_HEARTBEATS || !navigator.onLine) {
      return;
    }

    this.pressuredHeartbeats = 0;
    this.logging.trackEvent('memory_pressure_reload', { usedJsHeapMb, heapLimitMb });
    this.logging.markCleanShutdown(ShutdownReason.MemoryPressure);
    this.reload();
  }

  private reload(): void {
    window.location.reload();
  }

  private sample(): HeartbeatPayloadModel {
    const census = this.deviceCensus.census(
      navigator.userAgent, screen.width, screen.height, window.devicePixelRatio,
      this.nativeInformation(), 'deviceMemory' in navigator ? navigator.deviceMemory : undefined
    );
    const heap = this.deviceCensus.heapSample('memory' in performance ? performance.memory : undefined);
    const now = this.clock.now().toEpochMilli();
    const runtime = this.runtimeSample?.() ?? {};
    // Auth outranks everything: a signed-out display may look like it is playing, and that is the misleading case.
    const state = this.auth.state() === 'recovering' || this.auth.state() === 'revoked' ? HeartbeatState.AuthRecovering
      : navigator.onLine ? runtime.state ?? HeartbeatState.Starting : HeartbeatState.Offline;
    const feeds = { ...Object.fromEntries(Object.entries(this.feeds.feeds()).map(([feed, health]) => [feed, {
      lastSuccessAgoMs: health.lastSuccessAt === null ? null : Math.max(0, now - health.lastSuccessAt),
      consecutiveFailures: health.consecutiveFailures
    }])), ...runtime.feeds };
    // Spread order is the precedence rule: the shell's own measurements win over the runtime's contribution.
    return {
      ...runtime,
      ...census,
      ...heap,
      state,
      uptimeMs: Math.round(performance.now()),
      activeTimers: runtime.activeTimers ?? this.clock.activeTimers,
      tokenValid: this.auth.isTokenValid,
      lastRefreshAgoMs: this.auth.lastRefreshAt() === null ? null : Math.max(0, now - this.auth.lastRefreshAt()),
      refreshFailures: this.auth.refreshFailures(),
      // A display that cannot recover its own credentials has to be visible remotely, because nothing
      // on the device will ever fix it; lastRefreshAgoMs nearing the server window is the alert to watch.
      authState: this.auth.state(),
      accessTokenExpiresInMs: this.auth.accessTokenExpiresInMs,
      online: navigator.onLine,
      offlineEventsSinceStart: this.offlineEvents,
      consecutiveFeedFailures: Object.values(feeds).reduce((sum, health) => sum + health.consecutiveFailures, 0),
      feeds
    };
  }

  private nativeInformation(): NativeDeviceInformationModel | undefined {
    const information = 'deviceInformation' in window ? window.deviceInformation : undefined;

    if (typeof information === 'object' && information !== null && 'appVersionName' in information
      && typeof information.appVersionName === 'string') {
      return {
        appVersionName: information.appVersionName,
        chromiumVersion: 'chromiumVersion' in information && typeof information.chromiumVersion === 'string'
          ? information.chromiumVersion : undefined
      };
    }

    return undefined;
  }
}
